import { useMemo } from 'react';
import {
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ColumnDef, LineMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors, getChartStrokeWidth } from '../../theme/index.js';
import { createChartFormatter } from '../../utils/chart-formatter.js';
import { ChartTooltip } from '../../components/ChartTooltip.js';
import { prepCartesianSeries } from '../shared/cartesian-utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: LineMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function LineChart({ rows, columnDefs, mapping, config, width, height }: Props) {
  const series = useMemo(
    () =>
      rows.length === 0
        ? { labels: [], datasets: [] }
        : prepCartesianSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const datasets = useMemo(() => series.datasets ?? [], [series.datasets]);

  const chartData = useMemo(
    () =>
      series.labels.map((label, index) => {
        const point: Record<string, string | number> = { name: label };
        datasets.forEach((dataset) => {
          point[dataset.label] = dataset.values[index] ?? 0;
        });
        return point;
      }),
    [series.labels, datasets]
  );

  if (!series.labels.length || !datasets.length) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const datasetCount = datasets.length;
  const colors = getColorsForIntent(config.intent, datasetCount);
  const themeColors = getThemeColors();
  const strokeWidth = getChartStrokeWidth();

  const formatValue = createChartFormatter(datasets);

  return (
    <RechartsLine
      width={width ?? 400}
      height={height ?? 300}
      data={chartData}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
      <XAxis
        dataKey="name"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
      />
      <YAxis
        width="auto"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
        tickFormatter={(value) => formatValue(value, 0)}
      />
      <Tooltip content={<ChartTooltip formatter={formatValue} />} />
      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
      {datasets.map((dataset, index) => (
        <Line
          key={dataset.label}
          type="monotone"
          dataKey={dataset.label}
          stroke={colors[index % colors.length]}
          strokeWidth={strokeWidth}
          dot={{ r: 4, strokeWidth: strokeWidth - 0.5 }}
          activeDot={{ r: 6, strokeWidth: strokeWidth }}
        />
      ))}
    </RechartsLine>
  );
}
